import { type Header, type Table as TableType, flexRender } from "@tanstack/react-table";
import { ChevronDown, ChevronsUpDown, ChevronUp, Filter } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { Filters } from "./filters";

/** Kenglik va markazlash bo'yicha alohida qaraladigan ustunlar. */
const NARROW_COLUMNS = ["actions", "index", "select"];

interface TableHeaderProps<TData> {
  table: TableType<TData>;
  className?: string;
}

/**
 * Jadval sarlavhasi: nom + filtr + saralash.
 *
 * Filtrlar jadval TEPASIDA emas, aynan o'z ustunida turadi. Sabab:
 * tepadagi filtr paneli "bu maydon qaysi ustunga tegishli?" degan
 * savolni tug'diradi va ustunlar soni o'sgani sari panel o'sib boradi.
 * Ustundagi belgi esa faol filtrni ko'rsatib ham turadi — rangi
 * o'zgaradi.
 */
export function TableHeaderRow<TData>({ table, className }: Readonly<TableHeaderProps<TData>>) {
  const { t } = useTranslation();

  const sortLabel = (direction: false | "asc" | "desc"): string => {
    if (direction === "asc") return t("table.sortAsc");
    if (direction === "desc") return t("table.sortDesc");
    return t("table.sort");
  };

  /**
   * Filtr belgisi.
   *
   * Faol filtr RANG bilan emas, TO'LDIRILGAN nishon va fon bilan
   * ko'rsatiladi. Sabab: mavzuning `--primary` va `--muted-foreground`
   * qiymatlari yorug' mavzuda faqat yorqinlik bilan farq qiladi,
   * qorong'usida esa umuman bir xil — ya'ni "qidirilgan ustun" belgisi
   * ko'rinmay qolardi. To'ldirilgan shakl har qanday mavzuda ham,
   * rangni ajratmaydigan odam uchun ham ko'rinadi.
   */
  const filterButton = (header: Header<TData, unknown>) => {
    const active = Boolean(header.column.getFilterValue());

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "cursor-pointer rounded-sm p-0.5",
              active && "bg-primary text-primary-foreground",
            )}
            aria-label={t("common.filter")}
            aria-pressed={active}
          >
            <Filter className={cn("size-3.5", active ? "fill-current" : "text-muted-foreground")} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0">
          <Filters column={header.column} />
        </PopoverContent>
      </Popover>
    );
  };

  const sortButton = (header: Header<TData, unknown>) => {
    const direction = header.column.getIsSorted();

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="cursor-pointer"
            aria-label={sortLabel(direction)}
            onClick={header.column.getToggleSortingHandler()}
          >
            {direction === "asc" && <ChevronUp className="size-3.5" />}
            {direction === "desc" && <ChevronDown className="size-3.5" />}
            {!direction && <ChevronsUpDown className="text-muted-foreground size-3.5" />}
          </button>
        </TooltipTrigger>
        <TooltipContent>{sortLabel(direction)}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    // `sticky` — aylantirilganda sarlavha joyida qoladi. `-top-px`:
    // bir piksellik chegara aylantirishda sirg'alib chiqib ketmasin.
    <TableHeader className={cn("bg-secondary sticky -top-px z-10", className)}>
      {table.getHeaderGroups().map(group => (
        <TableRow key={group.id}>
          {group.headers.map(header => {
            const meta = header.column.columnDef.meta;
            const narrow = NARROW_COLUMNS.includes(header.column.id);

            return (
              <TableHead
                key={header.id}
                className={cn(
                  "h-9 border px-2 text-xs",
                  narrow && "text-center",
                  // Amallar ustuni gorizontal aylantirishda ham ko'rinib
                  // turadi: bloklash tugmasiga yetish uchun jadvalni
                  // oxirigacha surish kerak bo'lmasin.
                  header.column.id === "actions" && "bg-secondary sticky right-0 z-20",
                )}
                style={
                  meta?.width
                    ? { width: meta.width, minWidth: meta.width, maxWidth: meta.width }
                    : undefined
                }
              >
                <div
                  className={cn(
                    "flex items-center justify-between gap-2",
                    narrow && "justify-center gap-0",
                  )}
                >
                  {/* Filtrlangan ustun sarlavhasi ham quyuqlashadi: ko'z
                      avval nomga tushadi, belgiga emas.

                      `min-w-0` — flex elementining eng kichik kengligi
                      standart holatda MAZMUNIGA teng: uzun sarlavha
                      ("Ro'yxatdan o'tgan") qisqarmay, filtr va saralash
                      tugmalarini hujayradan tashqariga surib chiqarardi. */}
                  <span
                    className={cn(
                      "min-w-0 truncate",
                      Boolean(header.column.getFilterValue()) && "font-semibold",
                    )}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </span>

                  <div className="flex shrink-0 items-center gap-1">
                    {header.column.getCanFilter() && meta?.filterVariant && filterButton(header)}
                    {header.column.getCanSort() && sortButton(header)}
                  </div>
                </div>
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
}
